/*
 * Camada de comaptibilidade entre o Grid e o GridEx.
 *
 * O blueprint Grid foi substituído pelo GridEx.
 * O GridEx é uma versão aprimorada do Grid, que possui mais funcionalidades e é mais flexível.
 * O GridEx é compatível com o Grid, ou seja, você pode substituir o Grid pelo GridEx sem problemas.
 * Este arquivo foi criado para implementar esta camada de compatibilidade.
 */

import { findRels } from '@/helpers/rels'

/**
 * Mescla as ações definidas em dataSets com as ações definidas em gridExActions.
 *
 * @param {object} targetActions
 *    Ações do GridEx.
 *    Exemplo:
 *      {
 *        menu: {
 *          all: [...], // Todas as ações da página
 *          main: [...], // Ações principais da página
 *          side: [...], // Ações secundárias da página
 *          rest: [...], // Outras ações da página
 *          categories: [
 *            { name: 'Category1', actions: [...] }, // Categorias personalizadas
 *            { name: 'Category2', actions: [...] },
 *            // ...
 *          ]
 *        },
 *        pins: {
 *          all: [...], // Todas as ações fixadas da página
 *          main: [...], // Ações principais fixadas da página
 *          side: [...], // Ações secundárias fixadas da página
 *          rest: [...], // Outras ações fixadas da página
 *          categories: [
 *            { name: 'Category1', actions: [...] }, // Categorias personalizadas
 *            { name: 'Category2', actions: [...] },
 *            // ...
 *          ]
 *        }
 *      }
 *
 * @param {object} sourceLinks
 *    Ações definidas em coleções de links em sets de dados ou em seus registros (rows).
 * @returns {object} As ações mescladas.
 */
export function mergeActions(targetActions, sourceLinks) {
  // Verificando a existência e o conteúdo de links
  if (!(sourceLinks?.length > 0)) return targetActions

  // Se targetActions não contem menu ou pins então não é o objeto esperado.
  // Neste caso lançaremos exceção porque não é seguro continuar.
  if (!targetActions.menu || !targetActions.pins) {
    throw new Error('targetActions não contém menu ou pins.')
  }

  const menu = findRels(sourceLinks, 'Action')
  const mainMenu = findRels(menu, { includes: ['Primary'] })
  const sideMenu = findRels(menu, { includes: ['Secondary'] })
  const restMenu = findRels(menu, { excludes: ['Primary','Secondary'] })
  const pins = findRels(sourceLinks, ['Action', 'Pinned'] )
  const mainPins = findRels(pins, { includes: ['Primary'] })
  const sidePins = findRels(pins, { includes: ['Secondary'] })
  const restPins = findRels(pins, { excludes: ['Primary','Secondary'] })

  // Capturando as categorias de menus
  const menuCategories = menu.reduce((acc, action) => {
    const categoryName = action.category !== undefined ? action.category : null
    const categoryIndex = acc.findIndex(c => c.name === categoryName)

    if (categoryIndex === -1) {
      acc.push({ name: categoryName, actions: [action] })
    } else {
      acc[categoryIndex].actions.push(action)
    }
    return acc
  }, [])

  // Capturando as categorias de pins
  const pinsCategories = pins.reduce((acc, action) => {
    const categoryName = action.category !== undefined ? action.category : null
    const categoryIndex = acc.findIndex(c => c.name === categoryName)

    if (categoryIndex === -1) {
      acc.push({ name: categoryName, actions: [action] })
    } else {
      acc[categoryIndex].actions.push(action)
    }
    return acc
  }, [])

  // Mesclando as categorias de menus
  let mergedMenuCategories = targetActions.menu.categories.reduce((acc, existingCategory) => {
    const categoryIndex = menuCategories.findIndex(c => c.name === existingCategory.name)

    if (categoryIndex !== -1) {
      acc.push({
        name: existingCategory.name,
        actions: existingCategory.actions.concat(menuCategories[categoryIndex].actions)
      })
      menuCategories.splice(categoryIndex, 1) // Remove a categoria já processada
    } else {
      acc.push(existingCategory)
    }
    return acc
  }, []).concat(menuCategories) // Adiciona quaisquer categorias novas restantes

  // Mesclando as categorias de pins
  let mergedPinsCategories = targetActions.pins.categories.reduce((acc, existingCategory) => {
    const categoryIndex = pinsCategories.findIndex(c => c.name === existingCategory.name)

    if (categoryIndex !== -1) {
      acc.push({
        name: existingCategory.name,
        actions: existingCategory.actions.concat(pinsCategories[categoryIndex].actions)
      })
      pinsCategories.splice(categoryIndex, 1) // Remove a categoria já processada
    } else {
      acc.push(existingCategory)
    }
    return acc
  }, []).concat(pinsCategories) // Adiciona quaisquer categorias novas restantes

  const mergedActions = {
    menu: { ...targetActions.menu },
    pins: { ...targetActions.pins },
    states: { ...targetActions.states }
  }

  // Mesclando ações
  mergedActions.menu.categories = mergedMenuCategories
  mergedActions.menu.all = mergedActions.menu.all.concat(menu)
  mergedActions.menu.main = mergedActions.menu.main.concat(mainMenu)
  mergedActions.menu.side = mergedActions.menu.side.concat(sideMenu)
  mergedActions.menu.rest = mergedActions.menu.rest.concat(restMenu)

  mergedActions.pins.categories = mergedPinsCategories
  mergedActions.pins.all = mergedActions.pins.all.concat(pins)
  mergedActions.pins.main = mergedActions.pins.main.concat(mainPins)
  mergedActions.pins.side = mergedActions.pins.side.concat(sidePins)
  mergedActions.pins.rest = mergedActions.pins.rest.concat(restPins)

  return mergedActions
}
